import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { Organization, OrganizationPermissions, ProjectPermissions } from '../api/organization.schemas';
import { getOrganizationDetails } from '../api/organization.api';

/**
 * Hook to get organizations from user session.
 *
 * The session's `metadata_public.tenants[]` is the canonical list of
 * org memberships the user has (with their role + project assignments),
 * but the `tenants[i].name` is not authoritative — the orchestrator
 * persists it as `""` on SSO and MemberService only refreshes it on
 * admin writes. We fetch each org's authoritative data (name, dates,
 * future fields like logo/plan) from the BFF in parallel via React
 * Query. While a fetch is pending we fall back to whatever the session
 * carries; once the BFF responds the dashboard re-renders with the
 * fresh name.
 */
export function useOrganizations() {
  const session = useAuthStore((state) => state.session);
  const setOrganizations = useSettingsStore((state) => state.setOrganizations);
  const setSelectedOrganization = useSettingsStore((state) => state.setSelectedOrganization);
  const setOrganizationPermissions = useSettingsStore((state) => state.setOrganizationPermissions);
  const setProjectPermissions = useSettingsStore((state) => state.setProjectPermissions);
  const selectedOrganization = useSettingsStore((state) => state.selectedOrganization);
  const organizations = useSettingsStore((state) => state.organizations);

  const sessionTenants = session?.identity?.metadata_public?.tenants;

  // Stable list of tenant IDs the user is a member of (session is the
  // source of truth for membership; BFF only enriches with display
  // fields).
  const tenantIds = useMemo(
    () => (sessionTenants ?? []).map((t) => t.id),
    [sessionTenants],
  );

  // Fan-out parallel GETs to /organizations/{id}. React Query keeps a
  // per-id cache (staleTime below) and dedupes refetches across hook
  // consumers; the org switcher and any other caller sees the same
  // data without extra requests.
  const orgQueries = useQueries({
    queries: tenantIds.map((id) => ({
      queryKey: ['organization-details', id],
      queryFn: () => getOrganizationDetails(id),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });

  // Cheap stable signature of the fetched org data so memo deps are
  // primitives. Whenever any org's id|name pair changes the signature
  // changes and the merge memo re-runs.
  const fetchedSignature = orgQueries
    .map((q) => (q.data ? `${q.data.id}:${q.data.name}` : ''))
    .join('|');

  // Merge session memberships (id + role + projects) with BFF-fetched
  // display fields. Fallback chain for `name`: BFF response → session
  // metadata (almost always `""` post-SSO) → empty string.
  const sessionOrganizations = useMemo((): Organization[] => {
    if (!sessionTenants) return [];
    return sessionTenants.map((tenant, index) => {
      const fetched = orgQueries[index]?.data;
      return {
        id: tenant.id,
        name: fetched?.name || tenant.name || '',
        role: tenant.role as 'owner' | 'administrator' | 'developer' | 'auditor',
        created_at: fetched?.createdAt || new Date().toISOString(),
        updated_at: fetched?.updatedAt || new Date().toISOString(),
        metadata: {
          projects: tenant.projects || [],
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionTenants, fetchedSignature]);

  // Update store when session organizations change.
  //
  // Two guards keep us out of an infinite render loop:
  //   1. setOrganizations only fires when the list of ids changes
  //      (sessionOrganizations is rebuilt on every BFF data arrival;
  //      pushing identical content would re-emit Zustand → re-render
  //      → push again).
  //   2. We don't reactively update `selectedOrganization` from inside
  //      this effect. The store's setSelectedOrganization has heavy
  //      side effects (clears selectedProject/projects/permissions),
  //      so calling it on every BFF refresh cascades into re-renders
  //      across every consumer. Components that need the fresh name
  //      read it through `useSelectedOrganizationDetails` (see below).
  useEffect(() => {
    if (sessionOrganizations.length > 0) {
      // Compare a content signature (id + name) so we propagate both
      // membership changes AND BFF-enriched name updates, while
      // skipping no-op writes that would re-emit the Zustand slice
      // and re-run this effect.
      const currentSig = organizations.map((o) => `${o.id}:${o.name}`).join('|');
      const nextSig = sessionOrganizations.map((o) => `${o.id}:${o.name}`).join('|');
      if (currentSig !== nextSig) {
        setOrganizations(sessionOrganizations);
      }

      // Auto-select first organization if none selected.
      if (!selectedOrganization) {
        setSelectedOrganization(sessionOrganizations[0]);
        return;
      }

      // If the selected org is no longer in the user's tenants, fall
      // back to the first available one.
      const stillMember = sessionOrganizations.some(
        (org) => org.id === selectedOrganization.id,
      );
      if (!stillMember) {
        setSelectedOrganization(sessionOrganizations[0]);
      }
    } else if (sessionOrganizations.length === 0 && organizations.length > 0) {
      setOrganizations([]);
      setSelectedOrganization(null);
    }
  }, [sessionOrganizations, selectedOrganization, organizations, setOrganizations, setSelectedOrganization]);

  // Set default permissions when organization is selected. The
  // mapping below mirrors the V1 role matrix enforced by Cedar policies
  // in wya-platform-web-bff/configs/policies/. `usePermissions()`
  // can override these with the per-user BFF response when needed.
  // SCOPE behavior for developer/auditor (only see assigned projects)
  // is enforced server-side in handlers like getProjects.ts; the
  // booleans below describe whether the role has the *capability*
  // (subject to project scope where applicable).
  useEffect(() => {
    if (selectedOrganization) {
      const role = selectedOrganization.role;
      const isAdmin = role === 'owner' || role === 'administrator';
      const canCreate = isAdmin || role === 'developer';

      // Project assignment modes for non-admin org roles:
      //   - "all projects" — either an explicit wildcard entry
      //     `{id: "*"}` OR an empty array (legacy fallback for
      //     developer/auditor while data migrates). The user inherits
      //     their org-role on every project.
      //   - "scoped" — non-empty array without `*`. Per-project roles
      //     apply; the user only sees the listed projects.
      // Org admin/owner ignore this entirely (Cedar permits them
      // unconditionally).
      const projectAssignments = (
        selectedOrganization.metadata?.projects || []
      ) as Array<{ id: string; role?: string }>;
      const hasWildcardProject = projectAssignments.some(
        (p) => p.id === '*',
      );
      const isAllProjectsMode =
        hasWildcardProject ||
        (projectAssignments.length === 0 &&
          (role === 'developer' || role === 'auditor'));
      const hasAnyProjectWriteRole =
        !isAllProjectsMode &&
        projectAssignments.some((p) =>
          ['administrator', 'owner', 'developer'].includes(p.role || ''),
        );
      // Developer org-role with "all" mode can write to every project;
      // auditor org-role with "all" still cannot write. Scoped users
      // get write iff at least one assignment grants a write role.
      const canEditAnyProject =
        isAdmin ||
        (role === 'developer' && isAllProjectsMode) ||
        hasAnyProjectWriteRole;

      const defaultOrgPermissions: OrganizationPermissions = {
        canCreateProject: canCreate,
        canEditProject: canEditAnyProject,
        canDeleteProject: isAdmin, // delete remains org admin/owner only
        canManageMembers: isAdmin,
        canManageOrgSettings: isAdmin,
        canViewSso: isAdmin || role === 'auditor',
        canManageSso: isAdmin,
      };

      // Project-level defaults. `canManageSettings` here = "can edit
      // project configurations" — gated by Model B (admin/owner/dev
      // either at org or project level).
      const defaultProjectPermissions: ProjectPermissions = {
        canViewMetrics: true,
        canManageSettings: canEditAnyProject,
        canViewTables: true,
        canExportData: canEditAnyProject,
        canEditProject: canEditAnyProject,
        canDeleteProject: isAdmin,
      };

      setOrganizationPermissions(defaultOrgPermissions);
      setProjectPermissions(defaultProjectPermissions);
    }
  }, [selectedOrganization, setOrganizationPermissions, setProjectPermissions]);

  return {
    organizations: sessionOrganizations.length > 0 ? sessionOrganizations : organizations,
    isLoading: false, // No async loading since data comes from session
    error: null,
    refetch: () => {}, // No refetch needed since data comes from session
  };
}

/**
 * Returns the currently selected organization with BFF-enriched fields
 * (specifically the canonical `name`) merged in. Components that
 * display the org name in the header / breadcrumb should consume this
 * instead of `useSettingsStore(s => s.selectedOrganization)` directly,
 * which carries the empty name persisted by SSO callback / SCIM.
 *
 * Falls back to whatever the store has if the BFF hasn't responded
 * yet — the dashboard re-renders once /organizations/{id} resolves.
 */
export function useSelectedOrganizationDetails(): Organization | null {
  const selectedOrganization = useSettingsStore((state) => state.selectedOrganization);
  const organizations = useSettingsStore((state) => state.organizations);

  return useMemo(() => {
    if (!selectedOrganization) return null;
    const enriched = organizations.find((o) => o.id === selectedOrganization.id);
    return enriched ?? selectedOrganization;
  }, [selectedOrganization, organizations]);
}